import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Send, MessageCircle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { todayISO, formatDateES, computeClassBreakdown } from "@/lib/utils-app";

/**
 * V1.35 · "Enviar próximas citas" — genera UN único enlace de confirmación
 * que agrupa varias citas del alumno. NO redefine la experiencia: la
 * pantalla del alumno sigue siendo la de siempre; sólo aparece la lista de
 * varias citas dentro del mismo enlace.
 *
 * Ley 1 (Existencia): reutiliza `/api/appointments?student_id=` y la ruta
 * pública `/confirm?ids=...` (ya cableada en Confirm.jsx).
 * Ley 3 (Acción): filtra por defecto futuras, no canceladas y no asistidas.
 *
 * Props:
 *   open, onClose
 *   studentId          — obligatorio para cargar sus citas
 *   studentName, phone — opcionales (mejor UX). Si `phone` está presente
 *                        se abre wa.me/<phone>?text=…; si no, share genérico.
 */
export default function BulkWhatsAppDialog({
  open,
  onClose,
  studentId,
  studentName = "",
  phone = "",
}) {
  const [rows, setRows] = useState([]);          // [{id,date,time,end_time,status,attended,appointment_type}]
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("today");     // "today" | "custom"
  const [customFrom, setCustomFrom] = useState(todayISO());
  const [selected, setSelected] = useState({});  // {id: bool}
  // V1.36 · plantilla centralizada `bulk_upcoming` cargada al abrir el
  // diálogo. Si por lo que sea el fetch falla usamos un fallback interno
  // para no dejar al operador sin poder enviar.
  const [tplText, setTplText] = useState("");
  // V1.36 · Edición inline del mensaje final. Si `editedBody` es null se
  // usa el `messageBody` autogenerado; si tiene contenido, se envía tal cual.
  const [editing, setEditing] = useState(false);
  const [editedBody, setEditedBody] = useState(null);

  // Reset del editor cuando cambia de alumno o se cierra el diálogo
  useEffect(() => { setEditing(false); setEditedBody(null); }, [studentId, open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/kai/message-templates");
        if (cancelled) return;
        const t = (data?.templates?.whatsapp || {}).bulk_upcoming || "";
        setTplText(t);
      } catch (_) {
        if (!cancelled) setTplText("");
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open || !studentId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // V1.35 · include_shared=true trae también las citas donde el
        // alumno participa como acompañante compartido (evita -2 vs vista
        // del calendario cuando el alumno va en pareja / trío).
        const { data } = await api.get("/appointments", {
          params: { student_id: studentId, include_shared: true },
        });
        if (cancelled) return;
        setRows(Array.isArray(data) ? data : []);
      } catch (_) {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, studentId]);

  // Reset al abrir/cerrar
  useEffect(() => {
    if (open) {
      setMode("today");
      setCustomFrom(todayISO());
    }
  }, [open]);

  const fromDate = mode === "custom" ? customFrom : todayISO();

  const eligible = useMemo(() => {
    // Filtro: >= fromDate, status != cancelled, attended != true
    // + dedupe por id (por si el alumno aparece como titular y participante)
    const filtered = rows
      .filter((a) => (a.date || "") >= fromDate)
      .filter((a) => a.status !== "cancelled")
      .filter((a) => a.attended !== true);
    const seen = new Set();
    const uniq = [];
    for (const a of filtered) {
      if (a && a.id && !seen.has(a.id)) {
        seen.add(a.id);
        uniq.push(a);
      }
    }
    return uniq.sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));
  }, [rows, fromDate]);

  // Preselecciona todos los elegibles cada vez que cambia el filtro
  useEffect(() => {
    const next = {};
    eligible.forEach((a) => { next[a.id] = true; });
    setSelected(next);
  }, [eligible]);

  const chosenIds = useMemo(
    () => eligible.filter((a) => selected[a.id]).map((a) => a.id),
    [eligible, selected],
  );

  // V1.35 · Desglose clases vs. exámenes para reflejarlo en el mensaje
  // WhatsApp y en la vista previa. El alumno debe entender claramente que
  // la cita de examen práctico NO está confirmada por la DGT.
  const chosenBreakdown = useMemo(() => {
    const chosen = eligible.filter((a) => selected[a.id]);
    let clases = 0;
    let exams = 0;
    for (const a of chosen) {
      const t = a.appointment_type || "";
      if (t === "examen_teorico" || t === "examen_practico") exams += 1;
      else clases += 1;
    }
    return { clases, exams };
  }, [eligible, selected]);

  const bulkLink = useMemo(() => {
    if (chosenIds.length === 0) return "";
    const origin = window.location.origin;
    // V1.35c · ORDEN DE EMERGENCIA — URL corta e incorruptible.
    // Antes: /confirm?ids=a,b,c,d,e,f,g&viewer=X (300+ chars, WhatsApp
    // truncaba IDs y llegaban 5 en vez de 7). Ahora: /confirm?student=X
    // y la web del alumno consulta EXACTAMENTE la misma fuente de datos
    // que este modal (endpoint /appointments/public-by-student).
    const fromParam = mode === "custom" && customFrom ? `&from=${customFrom}` : "";
    return `${origin}/confirm?student=${studentId}${fromParam}`;
  }, [chosenIds.length, studentId, mode, customFrom]);

  // V1.36 · Redacción del mensaje WhatsApp — la ESTRUCTURA vive en la
  // plantilla `bulk_upcoming` del repositorio central (editable desde
  // Configuración → Plantillas de mensajes). Aquí sólo generamos los
  // bloques dinámicos (lista de sesiones y bloque examen) y sustituimos
  // los placeholders. Fallback interno si el fetch de la plantilla falla.
  const messageBody = useMemo(() => {
    const chosen = eligible.filter((a) => selected[a.id]);
    if (chosen.length === 0) return "";

    const firstName = (studentName || "").split(" ")[0] || "";
    const clases = chosen.filter(
      (a) => a.appointment_type !== "examen_teorico" && a.appointment_type !== "examen_practico",
    );
    const examenes = chosen.filter(
      (a) => a.appointment_type === "examen_teorico" || a.appointment_type === "examen_practico",
    );
    const totalClases = clases.reduce((acc, a) => {
      const b = computeClassBreakdown(a.time, a.end_time);
      return acc + (b?.numClasses || 0);
    }, 0);

    const capFirst = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
    const fmtRow = (a) => {
      try {
        const d = new Date(`${a.date}T00:00:00`);
        const weekday = capFirst(d.toLocaleDateString("es-ES", { weekday: "long" }));
        const dm = d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit" });
        const end = a.end_time || "";
        return `✅ ${weekday} ${dm} - ${a.time}${end ? ` a ${end}` : ""}`;
      } catch {
        return `✅ ${a.date} ${a.time}${a.end_time ? ` a ${a.end_time}` : ""}`;
      }
    };
    const fmtLongDate = (iso) => {
      try {
        const d = new Date(`${iso}T00:00:00`);
        return d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" });
      } catch {
        return iso;
      }
    };

    // Bloque prácticas — vacío si no hay clases
    let bloqueClases = "";
    if (clases.length > 0) {
      const rowsTxt = clases.map(fmtRow).join("\n");
      bloqueClases =
        "📚 PRÁCTICAS DE CIRCULACIÓN\n" +
        `${clases.length} ${clases.length === 1 ? "sesión programada" : "sesiones programadas"}\n` +
        (totalClases > 0
          ? `para un total de ${totalClases} ${totalClases === 1 ? "clase" : "clases"} de 45 minutos c/u\n`
          : "") +
        `${rowsTxt}\n\n`;
    }

    // Bloque examen — vacío si no hay exámenes
    let bloqueExamen = "";
    if (examenes.length > 0) {
      const prefix = clases.length > 0 ? "y un posible " : "";
      const isPractico = examenes.every((e) => e.appointment_type === "examen_practico");
      const isTeorico = examenes.every((e) => e.appointment_type === "examen_teorico");
      const title = isPractico
        ? "🚗 EXAMEN PRÁCTICO DGT"
        : isTeorico
          ? "📝 EXAMEN TEÓRICO DGT"
          : "🚗 EXAMEN DGT";
      const first = examenes[0];
      bloqueExamen =
        `${prefix}${title}\n` +
        "Fecha prevista:\n" +
        `${fmtLongDate(first.date)}\n` +
        "Estado:\n" +
        "⏳ No confirmado aún por DGT\n" +
        "La DGT suele comunicar la confirmación de convocatoria entre 3 y 7 días antes del examen y la hora exacta aproximadamente entre 48 y 72 horas antes.\n" +
        "\n";
    }

    // Plantilla central o fallback
    const FALLBACK =
      "Hola, {{nombre}}.\n" +
      "Estas son tus próximas actividades programadas:\n" +
      "\n" +
      "{{bloque_clases}}" +
      "{{bloque_examen}}" +
      "Por favor, revisa y confirma tu disponibilidad.\n" +
      "{{enlace}}";
    const template = tplText || FALLBACK;

    const ctx = {
      nombre: firstName || "alumno",
      bloque_clases: bloqueClases,
      bloque_examen: bloqueExamen,
      enlace: bulkLink || "",
    };
    let out = template;
    for (const [k, v] of Object.entries(ctx)) {
      out = out.split(`{{${k}}}`).join(String(v ?? ""));
    }
    return out;
  }, [eligible, selected, studentName, bulkLink, tplText]);

  // V1.36 · Mensaje que realmente se enviará: el editado por el operador si
  // existe, si no el generado por plantilla.
  const finalMessage = editedBody != null ? editedBody : messageBody;

  const waHref = useMemo(() => {
    if (!bulkLink || !finalMessage) return "";
    const clean = (phone || "").replace(/[^\d]/g, "");
    if (!clean) {
      return `https://wa.me/?text=${encodeURIComponent(finalMessage)}`;
    }
    return `https://wa.me/${clean}?text=${encodeURIComponent(finalMessage)}`;
  }, [bulkLink, finalMessage, phone]);

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="sm:max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="bulk-whatsapp-dialog"
      >
        <DialogHeader>
          <DialogTitle>Enviar próximas sesiones</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {studentName && (
            <div className="text-sm text-neutral-600">
              Alumno: <span className="font-semibold text-neutral-900">{studentName}</span>
            </div>
          )}

          <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-3 space-y-2">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-neutral-600">
              Enviar sesiones futuras
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="bulk-mode"
                checked={mode === "today"}
                onChange={() => setMode("today")}
                data-testid="bulk-mode-today"
              />
              <span>Desde hoy ({formatDateES(todayISO())})</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="bulk-mode"
                checked={mode === "custom"}
                onChange={() => setMode("custom")}
                data-testid="bulk-mode-custom"
              />
              <span>Desde fecha personalizada</span>
              <input
                type="date"
                value={customFrom}
                onChange={(e) => { setMode("custom"); setCustomFrom(e.target.value); }}
                disabled={mode !== "custom"}
                data-testid="bulk-custom-date"
                className="rounded-md border border-neutral-300 bg-white px-2 h-8 text-sm ml-1 disabled:opacity-50"
              />
            </label>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-neutral-600">
                Vista previa
              </div>
              <div className="text-[11px] text-neutral-500">
                Se enviarán{" "}
                <span className="font-semibold text-neutral-900" data-testid="bulk-preview-count">
                  {chosenIds.length}
                </span>{" "}
                {chosenIds.length === 1 ? "sesión" : "sesiones"}
                {chosenBreakdown.exams > 0 && (
                  <span
                    className="ml-1 text-violet-700 font-semibold"
                    data-testid="bulk-preview-breakdown"
                  >
                    ({chosenBreakdown.clases} {chosenBreakdown.clases === 1 ? "clase" : "clases"}
                    {" + "}
                    {chosenBreakdown.exams} {chosenBreakdown.exams === 1 ? "examen" : "exámenes"})
                  </span>
                )}
              </div>
            </div>
            {loading ? (
              <div className="text-xs text-neutral-500 py-3">Cargando sesiones…</div>
            ) : eligible.length === 0 ? (
              <div
                data-testid="bulk-preview-empty"
                className="text-xs text-neutral-500 py-3 rounded-lg border border-dashed border-neutral-300 text-center"
              >
                No hay sesiones futuras pendientes desde {formatDateES(fromDate)}.
              </div>
            ) : (
              <div
                className="space-y-1.5 max-h-64 overflow-y-auto"
                data-testid="bulk-preview-list"
              >
                {eligible.map((a) => {
                  const isExam = a.appointment_type === "examen_teorico" || a.appointment_type === "examen_practico";
                  return (
                    <label
                      key={a.id}
                      data-testid={`bulk-preview-row-${a.id}`}
                      className={
                        "flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs hover:bg-neutral-50 cursor-pointer " +
                        (isExam
                          ? "border-violet-300 bg-violet-50/60"
                          : "border-neutral-200 bg-white")
                      }
                    >
                      <input
                        type="checkbox"
                        checked={!!selected[a.id]}
                        onChange={() => toggle(a.id)}
                        data-testid={`bulk-preview-check-${a.id}`}
                      />
                      <span className="font-mono tabular-nums text-neutral-700">
                        {a.date}
                      </span>
                      <span className="font-mono tabular-nums text-neutral-500">
                        {a.time}{a.end_time ? `–${a.end_time}` : ""}
                      </span>
                      {isExam && (
                        <span
                          className="text-[10px] font-semibold text-violet-800 uppercase tracking-wider"
                          data-testid={`bulk-preview-exam-tag-${a.id}`}
                          title="Examen práctico — aún no confirmado por la DGT"
                        >
                          {a.appointment_type === "examen_practico" ? "Examen práctico" : "Examen teórico"}
                        </span>
                      )}
                      {!isExam && a.status && a.status !== "pending" && (
                        <span className="text-[10px] text-neutral-500 uppercase">
                          {a.status.replace("_", " ")}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            )}
            {chosenBreakdown.exams > 0 && (
              <div
                data-testid="bulk-preview-exam-note"
                className="mt-1.5 text-[10px] text-violet-800 bg-violet-50 border border-violet-200 rounded px-2 py-1 leading-snug"
              >
                Nota: los exámenes prácticos figuran <span className="font-semibold">aún NO confirmados por la DGT</span> — el alumno lo verá reflejado en el mensaje.
              </div>
            )}
          </div>

          {/* V1.36 · Vista previa del mensaje final que verá el alumno.
              Muestra la plantilla `bulk_upcoming` con los placeholders ya
              sustituidos. El operador puede tocarlo inline sin abrir el
              editor de Configuración. */}
          {messageBody && (
            <div>
              <div className="flex items-center justify-between mb-1.5 gap-2">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-neutral-600">
                  Vista previa del mensaje
                  {editedBody != null && (
                    <span
                      className="ml-1.5 text-[10px] text-orange-700 normal-case tracking-normal"
                      data-testid="bulk-message-edited-flag"
                    >
                      · editado
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {editing ? (
                    <button
                      type="button"
                      onClick={() => setEditing(false)}
                      data-testid="bulk-message-done"
                      className="text-[10px] font-semibold text-emerald-800 hover:text-emerald-900 underline underline-offset-2"
                    >
                      Hecho
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => { setEditedBody(editedBody ?? messageBody); setEditing(true); }}
                      data-testid="bulk-message-edit"
                      className="text-[10px] font-semibold text-orange-700 hover:text-orange-900 underline underline-offset-2"
                    >
                      Editar
                    </button>
                  )}
                  {editedBody != null && (
                    <button
                      type="button"
                      onClick={() => { setEditedBody(null); setEditing(false); }}
                      data-testid="bulk-message-reset"
                      className="text-[10px] font-semibold text-neutral-500 hover:text-neutral-800 underline underline-offset-2"
                      title="Volver al mensaje generado por la plantilla"
                    >
                      Restablecer
                    </button>
                  )}
                </div>
              </div>
              {editing ? (
                <textarea
                  data-testid="bulk-message-textarea"
                  value={editedBody != null ? editedBody : messageBody}
                  onChange={(e) => setEditedBody(e.target.value)}
                  rows={10}
                  className="w-full whitespace-pre-wrap break-words text-[11px] leading-snug bg-white border border-orange-300 rounded-lg px-3 py-2 max-h-56 overflow-y-auto font-sans text-neutral-900 focus:outline-none focus:ring-2 focus:ring-orange-500 resize-y"
                />
              ) : (
                <pre
                  data-testid="bulk-message-preview"
                  className="whitespace-pre-wrap break-words text-[11px] leading-snug bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 max-h-56 overflow-y-auto font-sans text-emerald-950"
                >
                  {finalMessage}
                </pre>
              )}
              <div className="mt-1 flex items-center justify-between gap-2 flex-wrap">
                <div className="text-[10px] text-neutral-500 flex-1 min-w-[180px]">
                  {editedBody != null
                    ? "Este envío usa el texto editado. Los futuros seguirán la plantilla global."
                    : "Editable inline (sólo este envío) o desde Configuración → Plantillas para todos."}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => {
                      try {
                        navigator.clipboard.writeText(finalMessage);
                        toast.success("Mensaje copiado");
                      } catch { toast.error("No se pudo copiar"); }
                    }}
                    data-testid="bulk-copy-message"
                    className="text-[10px] font-semibold text-neutral-700 hover:text-neutral-900 border border-neutral-300 rounded-md px-2 py-0.5 bg-white"
                    title="Copiar mensaje al portapapeles"
                  >
                    📋 Copiar mensaje
                  </button>
                  {phone && (
                    <button
                      type="button"
                      onClick={() => {
                        try {
                          navigator.clipboard.writeText(phone);
                          toast.success("Teléfono copiado");
                        } catch { toast.error("No se pudo copiar"); }
                      }}
                      data-testid="bulk-copy-phone"
                      className="text-[10px] font-semibold text-neutral-700 hover:text-neutral-900 border border-neutral-300 rounded-md px-2 py-0.5 bg-white"
                      title={`Copiar ${phone}`}
                    >
                      📋 Copiar teléfono
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary"
            data-testid="bulk-cancel-button"
          >
            Cancelar
          </button>
          <a
            href={chosenIds.length > 0 ? waHref : undefined}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => {
              if (chosenIds.length === 0) { e.preventDefault(); return; }
              // V1.36 · Auditoría best-effort — no bloqueamos la apertura si falla
              api.post("/whatsapp-logs", {
                student_id: studentId,
                appointment_ids: chosenIds,
                message: finalMessage,
                mode,
              }).catch(() => {});
            }}
            aria-disabled={chosenIds.length === 0}
            className={
              "inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl px-4 py-2.5 transition-colors h-11 text-sm " +
              (chosenIds.length === 0 ? "opacity-50 pointer-events-none" : "")
            }
            data-testid="bulk-send-whatsapp"
            title={phone ? `Abrir chat con ${phone}` : "Compartir mensaje por WhatsApp"}
          >
            <MessageCircle className="h-4 w-4" />
            📲 Abrir WhatsApp{phone ? " del alumno" : ""}
          </a>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
